import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { IconAlertTriangle, IconCircleCheck, IconCircleDashed, IconCircleX, IconMessageCircle2 } from "@tabler/icons";
import { caseChecks } from "./rules";
import { toneColor } from "./workspace";
import ActionButton from "src/components/shared/ActionButton";

const LEVEL = {
  fail: { word: "Fail", tone: "fail", Icon: IconCircleX },
  warn: { word: "Warning", tone: "warn", Icon: IconAlertTriangle },
  pass: { word: "Pass", tone: "pass", Icon: IconCircleCheck },
  skip: { word: "Not checked", tone: "skip", Icon: IconCircleDashed },
  noted: { word: "Noted by the customer", tone: "noted", Icon: IconMessageCircle2 },
};

// A reason longer than this folds to two lines with "More" (nadia-29: a
// red wall of whole question texts hid the line to act on).
const LONG = 90;
// The button says where it goes (georges-16): "Go to" alone said nothing.
export const goWords = (target) => (target?.type === "doc" ? "See the document" : "See the answer");

function CheckRow({ check, travellerName, onGo }) {
  const level = LEVEL[check.level];
  const [open, setOpen] = useState(false);
  const long = String(check.reason || "").length > LONG;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "20px minmax(0, 1fr) auto",
        columnGap: 1.25,
        alignItems: "start",
        py: 1,
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Box sx={{ color: toneColor(level.tone), pt: "1px", display: "flex" }} aria-hidden>
        <level.Icon size={19} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography fontWeight={600} sx={{ lineHeight: 1.35 }}>
          <Box component="span" sx={visuallyHidden}>
            {level.word}:{" "}
          </Box>
          {check.title}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.8125rem",
            lineHeight: 1.4,
            color: check.level === "pass" || check.level === "noted" ? "text.secondary" : toneColor(level.tone),
            ...(long && !open ? { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" } : {}),
          }}
        >
          {check.reason}
        </Typography>
        {long && (
          <Box component="button" type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} sx={{ p: 0, border: 0, bgcolor: "transparent", color: "primary.main", font: "inherit", fontSize: "0.8125rem", fontWeight: 600, cursor: "pointer", minHeight: 24 }}>
            {open ? "Less" : "More"}
            <Box component="span" sx={visuallyHidden}>{` about ${check.title}`}</Box>
          </Box>
        )}
      </Box>
      {check.target ? (
        <ActionButton
          variant="text"
          onClick={onGo}
          sx={{ minWidth: 0, mt: -0.75 }}
          aria-label={`${goWords(check.target)}: ${check.title}${travellerName ? ` for ${travellerName}` : ""}`}
        >
          {goWords(check.target)}
        </ActionButton>
      ) : (
        <span />
      )}
    </Box>
  );
}

CheckRow.propTypes = { check: PropTypes.object.isRequired, travellerName: PropTypes.string, onGo: PropTypes.func };

function Count({ n, tone, label }) {
  if (!n) return null;
  const Icon = LEVEL[tone].Icon;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: toneColor(tone) }}>
      <Icon size={16} aria-hidden />
      <Typography sx={{ fontWeight: 600, fontSize: "0.8125rem", color: "inherit" }}>
        {n} {label}
      </Typography>
    </Stack>
  );
}

Count.propTypes = { n: PropTypes.number, tone: PropTypes.string, label: PropTypes.string };

// The rule-based checks for the whole case, problems first. These are rules,
// not AI: the AI row below is a separate, clearly-labelled future feature.
export default function Checks({ data, onGo }) {
  const result = useMemo(() => caseChecks(data), [data]);
  const [showPassed, setShowPassed] = useState(false);
  const { totals } = result;
  const many = result.travellers.length > 1;
  const nothing = totals.fail + totals.warn + totals.pass + totals.skip + (totals.noted || 0) === 0;

  return (
    <Card variant="outlined" component="section" aria-labelledby="case-checks-title">
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
          <Typography variant="h6" component="h2" id="case-checks-title">
            Checks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Rules on the answers and documents
          </Typography>
        </Stack>

        {!nothing && (
          <Stack direction="row" spacing={2} mt={1} mb={0.5} flexWrap="wrap" useFlexGap>
            <Count n={totals.fail} tone="fail" label="to fix" />
            <Count n={totals.warn} tone="warn" label="to look at" />
            <Count n={totals.pass} tone="pass" label="passed" />
            <Count n={totals.skip} tone="skip" label="not checked" />
            <Count n={totals.noted} tone="noted" label="noted by the customer" />
          </Stack>
        )}

        {nothing && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            Nothing to check yet: this visa asks no required answers or documents.
          </Typography>
        )}

        {result.travellers.map((traveller) => {
          const problems = traveller.checks.filter((c) => c.level !== "pass");
          const passed = traveller.checks.filter((c) => c.level === "pass");
          const shown = showPassed ? [...problems, ...passed] : problems;
          if (!many && !shown.length) return null;
          return (
            <Box key={traveller.requestId} mt={1}>
              {many && (
                <Typography
                  variant="subtitle2"
                  component="h3"
                  sx={{ fontWeight: 700, mt: 1.5, mb: 0.5, display: "flex", justifyContent: "space-between", gap: 1 }}
                >
                  <span>{traveller.name}</span>
                  {!problems.length && (
                    <Box component="span" sx={{ color: toneColor("pass"), fontWeight: 600 }}>
                      All {passed.length} passed
                    </Box>
                  )}
                </Typography>
              )}
              {shown.map((check) => (
                <CheckRow
                  key={check.id}
                  check={check}
                  travellerName={many ? traveller.name : ""}
                  onGo={() => onGo(traveller.index, check.target)}
                />
              ))}
            </Box>
          );
        })}

        {!many && totals.pass > 0 && totals.fail + totals.warn + totals.skip === 0 && !showPassed && (
          <Typography mt={1} sx={{ color: toneColor("pass"), fontWeight: 600 }}>
            All {totals.pass} checks passed.
          </Typography>
        )}

        {totals.pass > 0 && (
          <ActionButton variant="text" sx={{ mt: 0.5, ml: -1.5 }} onClick={() => setShowPassed((v) => !v)} aria-expanded={showPassed}>
            {showPassed ? "Hide passed checks" : `Show ${totals.pass} passed ${totals.pass === 1 ? "check" : "checks"}`}
          </ActionButton>
        )}

      </CardContent>
    </Card>
  );
}

Checks.propTypes = { data: PropTypes.object.isRequired, onGo: PropTypes.func.isRequired };
